/**
 * Option lists shared by the org-scoped conversation reports.
 *
 * One fetch of `/reports/conversations/org-filters` serves both the Location
 * Performance and Conversation Transfer drawers: timezones, the
 * Location → Branch → Department cascade, and the dimensions a report may
 * group by.
 *
 * The cascade is narrowed in the browser — each branch carries its
 * `locationId`, each department its `branchId` — so choosing a location does
 * not refetch and make the dependent dropdowns flicker.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface FilterOption {
  label: string
  value: string
}
export interface TimezoneOption extends FilterOption {
  offset: string
}
export interface BranchOption extends FilterOption {
  locationId: string | null
}
export interface DepartmentOption extends FilterOption {
  branchId: string | null
  locationId: string | null
}

export interface OrgFilterOptions {
  timezones: TimezoneOption[]
  locations: FilterOption[]
  branches: BranchOption[]
  departments: DepartmentOption[]
  groupByOptions: FilterOption[]
}

const EMPTY: OrgFilterOptions = {
  timezones: [],
  locations: [],
  branches: [],
  departments: [],
  groupByOptions: [],
}

export function useReportOrgFilters(): { options: OrgFilterOptions; loading: boolean } {
  const [options, setOptions] = useState<OrgFilterOptions>(EMPTY)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const res = await api.get<OrgFilterOptions>('/reports/conversations/org-filters')
        if (!cancelled) setOptions({ ...EMPTY, ...res })
      } catch {
        if (!cancelled) setOptions(EMPTY)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { options, loading }
}

/**
 * Narrow branches to the chosen locations, and departments to the chosen
 * branches (falling back to their location when a department is attached
 * straight to one). An empty selection means "no narrowing", not "nothing".
 */
export function useCascade(
  options: OrgFilterOptions,
  locationIds: readonly string[],
  branchIds: readonly string[],
): { branches: BranchOption[]; departments: DepartmentOption[] } {
  const narrow = useCallback((): { branches: BranchOption[]; departments: DepartmentOption[] } => {
    const branches =
      locationIds.length === 0
        ? options.branches
        : options.branches.filter((b) => b.locationId !== null && locationIds.includes(b.locationId))

    const visibleBranchIds = new Set(branches.map((b) => b.value))
    const departments = options.departments.filter((d) => {
      if (branchIds.length > 0) return d.branchId !== null && branchIds.includes(d.branchId)
      if (locationIds.length > 0) {
        return (
          (d.branchId !== null && visibleBranchIds.has(d.branchId))
          || (d.locationId !== null && locationIds.includes(d.locationId))
        )
      }
      return true
    })
    return { branches, departments }
  }, [options, locationIds, branchIds])

  return narrow()
}
